import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { LinkedEntityList } from 'app/agriculture/components/LinkedEntityList/LinkedEntityList';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { useSnapshot } from 'app/agriculture/queries/snapshots/useSnapshot';
import { TankIndicator } from 'app/agriculture/routes/Tanks/TankIndicator/TankIndicator';
import { LinkIcon } from 'app/agriculture/icons';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { validateLevelSnapshot } from 'app/agriculture/routes/Devices/utils/validateLevelSnapshot';
import { useDevicesByType } from 'app/agriculture/queries/devices/useDevicesByType';
import { useMemo } from 'react';
import { Device } from 'app/agriculture/queries/devices/types/Device';

interface TankLevelComponentProps {
    infrastructureId: string;
}

export function TankLevelComponent({ infrastructureId }: TankLevelComponentProps) {
    const tankSensors = useDevicesByType('Level Monitor');

    const tankSensorsByInfrastructureId = useMemo(() => {
        const map = new Map<string, Device>();
        for (const [, tankSensor] of tankSensors) {
            const { infrastructureId } = tankSensor;
            if (infrastructureId != null) {
                map.set(infrastructureId, tankSensor);
            }
        }
        return map;
    }, [tankSensors]);

    const tankSensor = useMemo(
        () => tankSensorsByInfrastructureId.get(infrastructureId),
        [tankSensorsByInfrastructureId, infrastructureId],
    );

    const levelSnapshot = useSnapshot({
        parentId: tankSensor?.id,
        snapshotType: tankSensor?.brand === 'Gasbot' ? 'GasbotLevel' : 'Level',
        shelfLife: { hours: tankSensor?.brand === 'Gasbot' ? 12 : 1 },
    });

    if (!tankSensor) return null;

    const validatedLevelSnapshot = validateLevelSnapshot(levelSnapshot, { device: tankSensor });

    return (
        <ContentBox.Section disableDivider>
            <SimpleGrid columns={3}>
                <TankIndicator level={validatedLevelSnapshot?.percentageRemaining ?? null} />
                <StatDisplay
                    name="Remaining"
                    value={
                        validatedLevelSnapshot?.volumeRemaining == null
                            ? null
                            : (validatedLevelSnapshot.volumeRemaining * 1000).toFixed(1)
                    }
                    unit="L"
                />
                <StatDisplay
                    name="Linked Device"
                    value={<LinkedEntityList entityIds={[tankSensor.id]} icon={<LinkIcon />} />}
                    emptyStateMessage="N/A"
                    decimalPlaces={1}
                />
            </SimpleGrid>
        </ContentBox.Section>
    );
}
